import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import { callCenterApi } from '@/_services/api/callCenter';
import { toast } from 'react-toastify';
import { BsGear } from 'react-icons/bs';

const QueueConfigModal = ({ show, onHide, campaignId, campaignName, campaign }) => {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (show && campaignId) {
            fetchConfig();
        }
    }, [show, campaignId]);

    const fetchConfig = async () => {
        try {
            setLoading(true);
            const res = await callCenterApi.getCampaignConfig(campaignId);
            setConfig(res);
        } catch (error) {
            console.error('Error fetching config:', error);
            toast.error(error?.message || 'Failed to load queue configuration');
            setConfig(null);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await callCenterApi.updateCampaignConfig(campaignId, { settings: config.settings });
            toast.success('Configuration updated successfully');
            onHide();
        } catch (error) {
            console.error('Error saving config:', error);
            toast.error(error?.message || 'Failed to update configuration');
        } finally {
            setSaving(false);
        }
    };

    const updateSetting = (category, field, value) => {
        const newConfig = { ...config };
        if (category) {
            newConfig.settings[category][field] = Number(value);
        } else {
            newConfig.settings[field] = Number(value);
        }
        setConfig(newConfig);
    };

    if (!config && loading) {
        return (
            <Modal show={show} onHide={onHide} centered>
                <Modal.Body className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">Loading configuration...</p>
                </Modal.Body>
            </Modal>
        );
    }

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center">
                    <BsGear className="me-2 text-primary mr-2" />
                    Queue Configuration: {campaignName}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {config && (
                    <Form>
                        <h2>General Settings:</h2>
                        <Row className="mb-4">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Concurrent Calls</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={config.settings.concurrentCalls}
                                        onChange={(e) => updateSetting(null, 'concurrentCalls', e.target.value)}
                                        min="1"
                                        max="10"
                                    />
                                    <Form.Text className="text-muted">Max simultaneous calls</Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                        <h2>Retry Settings:</h2>
                        <Row className="mb-4">
                            <Col md={6} className="mb-3">
                                <Form.Group>
                                    <Form.Label>Max Retries</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={config.settings.retrySettings.maxRetries}
                                        onChange={(e) => updateSetting('retrySettings', 'maxRetries', e.target.value)}
                                        min="0"
                                        max="10"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} className="mb-3">
                                <Form.Group>
                                    <Form.Label>No Answer Retry Delay (sec)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        value={config.settings.retrySettings.noAnswerRetryDelay}
                                        onChange={(e) =>
                                            updateSetting('retrySettings', 'noAnswerRetryDelay', e.target.value)
                                        }
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} className="mb-3">
                                <Form.Group>
                                    <Form.Label>Busy Retry Delay (sec)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        value={config.settings.retrySettings.busyRetryDelay}
                                        onChange={(e) =>
                                            updateSetting('retrySettings', 'busyRetryDelay', e.target.value)
                                        }
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} className="mb-3">
                                <Form.Group>
                                    <Form.Label>Failed Retry Delay (sec)</Form.Label>
                                    <Form.Control
                                        min="0"
                                        type="number"
                                        value={config.settings.retrySettings.failedRetryDelay}
                                        onChange={(e) =>
                                            updateSetting('retrySettings', 'failedRetryDelay', e.target.value)
                                        }
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <h2>Rate Limiting:</h2>
                        <Row className="mb-3">
                            <Col md={4} className="mb-3">
                                <Form.Group>
                                    <Form.Label>Calls Per Minute</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        max="60"
                                        value={config.settings.rateLimiting.callsPerMinute}
                                        onChange={(e) =>
                                            updateSetting('rateLimiting', 'callsPerMinute', e.target.value)
                                        }
                                    />
                                </Form.Group>
                            </Col>
                            {/* TODO: IMPEMENT THIS IN BACKEND: BUT CONTACT COOLDOWN BASED ON TEAM OR WHOLE SYSTEM */}
                            {/* <Col md={4} className="mb-3">
                                <Form.Group>
                                    <Form.Label>Calls Per Contact</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        value={config.settings.rateLimiting.callsPerContact}
                                        onChange={(e) =>
                                            updateSetting('rateLimiting', 'callsPerContact', e.target.value)
                                        }
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4} className="mb-3">
                                <Form.Group>
                                    <Form.Label>Contact Cooldown (sec)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={config.settings.rateLimiting.contactCooldown}
                                        onChange={(e) =>
                                            updateSetting('rateLimiting', 'contactCooldown', e.target.value)
                                        }
                                    />
                                </Form.Group>
                            </Col> */}
                        </Row>
                    </Form>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={saving}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={saving}>
                    {saving && <Spinner animation="border" size="sm" className="me-2" />}
                    Save Configuration
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default QueueConfigModal;
